import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, MessageCircle, Mail, X, AlertTriangle } from 'lucide-react';
import communicationService from '../../services/communicationService';
import RecipientInput from './RecipientInput';
import MentionAutocomplete, { useMention } from './MentionAutocomplete';

const SendMessageForm = ({ clientId, caseId, clientName, onSend, onCancel }) => {
  const [messageType, setMessageType] = useState('sms');
  const [content, setContent] = useState('');
  const [subject, setSubject] = useState('');
  const [toPhones, setToPhones] = useState([]);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [groupConflict, setGroupConflict] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Mention handling
  const textareaRef = useRef(null);
  const { mentionState, handleInputChange, insertMention, closeMention } = useMention(textareaRef);

  const loadClientInfo = useCallback(async () => {
    try {
      // Load actual client data from API
      const api = await import('../../services/api');
      const response = await api.default.get(`/clients/${clientId}`);
      const client = response.data;

      // Pre-populate with client's contact info
      if (client.phone) {
        setToPhones([client.phone]);
      }
      if (client.email) {
        setToEmails([client.email]);
      }
    } catch (error) {
      console.error('Error loading client info:', error);
      // Fallback to placeholder values
      setToPhones(['+1-555-0123']);
      setToEmails(['client@example.com']);
    }
  }, [clientId]);

  useEffect(() => {
    // In a real app, you'd fetch client details to get phone/email
    // For now, we'll use placeholder logic
    loadClientInfo();
  }, [loadClientInfo]);

  // Check for group SMS conflicts when phone recipients change
  useEffect(() => {
    const checkConflicts = async () => {
      // Only check when there are 2+ phone recipients (group message)
      if (messageType !== 'sms' || toPhones.length < 2) {
        setGroupConflict(null);
        return;
      }

      setCheckingConflicts(true);
      try {
        // Normalize phone numbers before checking
        const normalizedPhones = toPhones
          .map(phone => communicationService.normalizePhoneNumber(phone))
          .filter(Boolean);

        if (normalizedPhones.length < 2) {
          setGroupConflict(null);
          return;
        }

        const result = await communicationService.checkGroupSmsConflicts(normalizedPhones);
        if (result.has_conflict) {
          setGroupConflict(result);
        } else {
          setGroupConflict(null);
        }
      } catch (err) {
        console.error('Error checking conflicts:', err);
        setGroupConflict(null);
      } finally {
        setCheckingConflicts(false);
      }
    };

    // Debounce the conflict check
    const timeoutId = setTimeout(checkConflicts, 300);
    return () => clearTimeout(timeoutId);
  }, [toPhones, messageType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);

    try {
      const messageData = {
        type: messageType,
        content: content.trim(),
        client_id: clientId,
        case_id: caseId,
      };

      if (messageType === 'sms') {
        if (toPhones.length === 0) {
          throw new Error('Please add at least one phone number');
        }
        // Normalize and validate all phone numbers
        const normalizedPhones = [];
        for (const phone of toPhones) {
          const normalized = communicationService.normalizePhoneNumber(phone);
          if (!normalized) {
            throw new Error(`Invalid phone number: ${phone}`);
          }
          normalizedPhones.push(normalized);
        }
        // Send to all recipients with normalized numbers (backend supports multiple)
        messageData.to_phones = normalizedPhones;
      } else if (messageType === 'email') {
        if (toEmails.length === 0) {
          throw new Error('Please add at least one email address');
        }
        if (!subject.trim()) {
          throw new Error('Please enter an email subject');
        }
        messageData.subject = subject.trim();
        // Backend supports a single To but multiple CC. Send the first address as
        // To and fold any additional recipients into CC so they still receive the
        // email instead of being silently dropped.
        messageData.to_email = toEmails[0];
        const combinedCc = [...toEmails.slice(1), ...ccEmails];
        messageData.cc_addresses = combinedCc.length > 0 ? combinedCc : undefined;
        messageData.bcc_addresses = bccEmails.length > 0 ? bccEmails : undefined;
      }

      await onSend(messageData);

      // Clear form after successful send
      setContent('');
      setSubject('');
      setToPhones([]);
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setContent('');
    setSubject('');
    setToPhones([]);
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setError('');
    setGroupConflict(null);
    onCancel();
  };

  const getCharacterLimit = () => {
    // SMS: Twilio supports up to 1600 chars with automatic segmentation
    // Email: Much more flexible, set high limit
    return messageType === 'sms' ? 1600 : 50000;
  };

  const isFormValid = () => {
    if (!content.trim()) return false;

    if (messageType === 'sms') {
      // Don't allow sending if there's a group conflict
      if (groupConflict) return false;
      return toPhones.length > 0;
    } else if (messageType === 'email') {
      return toEmails.length > 0 && subject.trim();
    }

    return false;
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Send Message to {clientName}</h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Message Type Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Message Type
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMessageType('sms')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  messageType === 'sms'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                SMS
              </button>
              <button
                type="button"
                onClick={() => setMessageType('email')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  messageType === 'email'
                    ? 'bg-gray-50 border-gray-200 text-yellow-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
            </div>
          </div>

          {/* Recipient Information */}
          {messageType === 'sms' ? (
            <>
              <RecipientInput
                recipients={toPhones}
                onChange={setToPhones}
                type="phone"
                label="To (Phone Numbers)"
                placeholder="+1 (555) 123-4567"
              />

              {/* Group SMS conflict warning */}
              {groupConflict && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      Group SMS Conflict
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      {groupConflict.conflicting_phones?.map(p => p.name || p.phone).join(', ')}{' '}
                      {groupConflict.conflicting_phones?.length === 1 ? 'is' : 'are'} already in the group conversation "{groupConflict.existing_conversation?.title}".
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      A phone number can only be in one group conversation at a time.
                      Remove the conflicting recipient(s) or send as individual SMS messages.
                    </p>
                  </div>
                </div>
              )}

              {/* Checking conflicts indicator */}
              {checkingConflicts && toPhones.length >= 2 && (
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                  Checking for group messaging conflicts...
                </div>
              )}
            </>
          ) : (
            <>
              <RecipientInput
                recipients={toEmails}
                onChange={setToEmails}
                type="email"
                label="To (Email Addresses)"
                placeholder="email@example.com"
              />

              <RecipientInput
                recipients={ccEmails}
                onChange={setCcEmails}
                type="email"
                label="CC (Optional)"
                placeholder="cc@example.com"
              />

              <RecipientInput
                recipients={bccEmails}
                onChange={setBccEmails}
                type="email"
                label="BCC (Optional)"
                placeholder="bcc@example.com"
              />
            </>
          )}

          {/* Email Subject */}
          {messageType === 'email' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Message subject"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                required
              />
            </div>
          )}

          {/* Message Content */}
          <div className="relative">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Message
              <span className="text-gray-400 font-normal ml-2">
                (Type @ to mention team members)
              </span>
            </label>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => {
                const newValue = e.target.value;
                setContent(newValue);
                handleInputChange(e, newValue);
              }}
              placeholder={`Enter your ${messageType === 'sms' ? 'text message' : 'email content'}...`}
              rows={messageType === 'sms' ? 6 : 10}
              maxLength={getCharacterLimit()}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-y min-h-[120px]"
              required
            />

            {/* Mention Autocomplete Dropdown */}
            <MentionAutocomplete
              isOpen={mentionState.isOpen}
              searchQuery={mentionState.searchQuery}
              position={mentionState.position}
              onSelect={(user) => {
                const { newText, newCursorPos } = insertMention(content, user);
                setContent(newText);
                closeMention();
                // Focus and set cursor position
                setTimeout(() => {
                  if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
                  }
                }, 0);
              }}
              onClose={closeMention}
            />
            
            <div className="flex justify-between items-center mt-2">
              <span className={`text-sm ${
                content.length > getCharacterLimit() * 0.9 ? 'text-red-600' : 'text-gray-500'
              }`}>
                {content.length} / {getCharacterLimit()} characters
              </span>
              
              {messageType === 'sms' && content.length > 160 && (
                <span className="text-sm text-amber-600" title="Long messages are automatically split into segments (153 chars each).">
                  This will be sent as {Math.ceil(content.length / 153)} segments
                </span>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={!isFormValid() || sending}
              className="flex-1 bg-yellow-500 text-white py-3 px-4 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send {messageType === 'sms' ? 'SMS' : 'Email'}
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendMessageForm;